import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import ChildSelector from "@/pages/veli/components/ChildSelector";
import { useVeliChild, useChildExamResults } from "@/hooks/useVeli";

export default function DenemeSonuclari() {
  const { children, activeChild, activeUserId, setActiveUserId, loading: childrenLoading } =
    useVeliChild();
  const { data, loading, error, reload } = useChildExamResults(activeChild?.userId ?? null);

  if (childrenLoading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;

  if (children.length === 0) {
    return (
      <div className="space-y-6">
        <SectionHeader
          title="Deneme Sonuçları"
          description="Öğrencinin deneme sınavı sonuçlarını takip et."
        />
        <EmptyState
          icon="ri-file-text-line"
          title="Henüz öğrenci hesabı bağlanmadı"
          description="Çocuğunuzun hesabını eşleştirdiğinizde deneme sonuçları burada görünecek."
        />
        <PanelLinks
          links={[{ label: "Çocuğunu Eşleştir", href: "/veli/profil", icon: "ri-link" }]}
        />
      </div>
    );
  }

  const list = data ?? [];

  const latest = list[0];
  const best = list.length
    ? list.reduce((max, r) => (r.net > max.net ? r : max), list[0])
    : null;
  const avgNet = list.length
    ? Math.round((list.reduce((s, r) => s + r.net, 0) / list.length) * 10) / 10
    : 0;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Deneme Sonuçları"
        description={`${activeChild?.fullName ?? "Öğrencinin"} deneme sınavı sonuçları ve net gelişimi.`}
      />
      <ChildSelector
        children={children}
        activeUserId={activeUserId}
        onSelect={setActiveUserId}
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-file-text-line"
          title="Henüz deneme sonucu yok"
          description="İlk deneme tamamlandığında sonuçlar ve analiz burada görünecek."
        />
      ) : (
        <>
          {/* Özet kartlar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">Son deneme neti</p>
              <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">
                {latest ? latest.net : "—"}
              </p>
              {latest && (
                <p className="mt-1 text-xs text-foreground-500">
                  {latest.name} · {latest.date}
                </p>
              )}
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">En yüksek net</p>
              <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">
                {best ? best.net : "—"}
              </p>
              {best && (
                <p className="mt-1 text-xs text-foreground-500">{best.name}</p>
              )}
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-xs font-medium text-foreground-500">Ortalama net</p>
              <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">
                {avgNet || "—"}
              </p>
              <p className="mt-1 text-xs text-foreground-500">{list.length} deneme</p>
            </div>
          </div>

          {/* Sonuç listesi */}
          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/60">
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Deneme</th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground-700">Tarih</th>
                    <th className="px-4 py-3 text-center font-semibold text-foreground-700">Doğru</th>
                    <th className="px-4 py-3 text-center font-semibold text-foreground-700">Yanlış</th>
                    <th className="px-4 py-3 text-center font-semibold text-foreground-700">Boş</th>
                    <th className="px-4 py-3 text-center font-semibold text-foreground-700">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.id} className="border-b border-background-200 hover:bg-background-100/40 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-950">{r.name}</td>
                      <td className="px-4 py-3 text-foreground-600">{r.date}</td>
                      <td className="px-4 py-3 text-center text-accent-700 font-semibold">{r.correct}</td>
                      <td className="px-4 py-3 text-center text-red-600 font-semibold">{r.wrong}</td>
                      <td className="px-4 py-3 text-center text-foreground-500">{r.blank}</td>
                      <td className="px-4 py-3 text-center text-foreground-950 font-bold">{r.net}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <PanelLinks
        links={[
          { label: "Haftalık Rapor", href: "/veli/haftalik-rapor", icon: "ri-bar-chart-line" },
          { label: "AI Öğrenci Analizi", href: "/veli/ai-analiz", icon: "ri-brain-line" },
        ]}
      />
    </div>
  );
}